import { Components, Theme } from '@mui/material'
import { ChevronDownOMIcon } from '@omnea-app/icons'

declare module '@mui/material/AccordionSummary' {
  interface AccordionSummaryOwnProps {
    /** Sits the content on its container's own content edge, keeping the row it tints wider than that. */
    'data-flush'?: boolean
  }
}

declare module '@mui/material/AccordionDetails' {
  interface AccordionDetailsProps {
    /** Drops the horizontal gutter, so the content sits on its container's own content edge. */
    'data-flush'?: boolean
  }
}

export const MuiAccordion: Components<Omit<Theme, 'components'>>['MuiAccordion'] = {
  defaultProps: {
    disableGutters: true,
  },
  variants: [
    {
      // Rounded is our default shape; opt into square corners with `square`
      props: { square: true },
      style: {
        '&, &:first-of-type, &:last-of-type': {
          borderRadius: 0,
        },
        '& .MuiAccordionSummary-root': {
          borderRadius: 0,
        },
      },
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: theme.palette.bg.invisible,
      '&.Mui-disabled': {
        backgroundColor: theme.palette.bg.invisible,
      },
      color: theme.palette.text.default,
      // MUI uses this to apply a default white highlight (via --Paper-overlay)
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&, &:first-of-type, &:last-of-type': {
        borderRadius: theme.shape['radius-lg'],
      },
      '& .MuiAccordionSummary-root': {
        borderRadius: theme.shape['radius-lg'],
      },
      // Remove border between Accordions when multiple are used together
      '&::before': {
        display: 'none',
      },
    }),
  },
}

export const MuiAccordionSummary: Components<Omit<Theme, 'components'>>['MuiAccordionSummary'] = {
  defaultProps: {
    expandIcon: <ChevronDownOMIcon size="20px" />,
  },
  variants: [
    {
      props: { 'data-flush': true },
      // MUI pins the root to `width: 100%` because a `<button>` shrink-wraps in the `all: unset`
      // heading above it, so the width has to grow with the margin — on its own the margin slides
      // the row sideways instead of widening it.
      style: ({ theme }) => {
        const bleed = theme.spacing(2)

        return {
          marginInline: `-${bleed}`,
          paddingInline: bleed,
          width: `calc(100% + ${bleed} * 2)`,
        }
      },
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: 'unset',
      '&.Mui-expanded': {
        minHeight: 'unset',
      },
      padding: theme.spacing(0, 4, 0, 3),
      '&:hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
    }),
    content: ({ theme }) => ({
      '&, &.Mui-expanded': {
        margin: theme.spacing(2, 0),
      },
      ...theme.typography['body-semiBold'],
    }),
    expandIconWrapper: ({ theme }) => ({
      padding: theme.spacing(1),
      color: theme.palette.fg.muted,
    }),
  },
}

export const MuiAccordionDetails: Components<Omit<Theme, 'components'>>['MuiAccordionDetails'] = {
  variants: [{ props: { 'data-flush': true }, style: { paddingBlockEnd: 0, paddingInline: 0 } }],
  styleOverrides: {
    root: ({ theme }) => ({
      padding: theme.spacing(2, 4, 4, 3),
    }),
  },
}

export const MuiAccordionActions: Components<Omit<Theme, 'components'>>['MuiAccordionActions'] = {}
